import type { Message, Part, Session } from "@mux/sdk/v2"
import type { MuxClient } from "@mux/sdk/v2/client"
import { cmpMessage } from "@/context/global-sync/session/message-reducer"
import { removeStarterMessage, type StarterMessage } from "@/context/session/starters"
import type { StarterPageRepository } from "@/storage/starter-pages"
import { HttpError } from "@/transport/http"
import { CreateRequestError } from "@/transport/sdk-request"
import { forkSessionAtMessage, loadTranscriptThrough, startSessionFromStarter, type SessionSnapshot } from "@/transport/session-activation"
import type { TurnDiffMessage } from "@/transport/turn-diffs"
import { Identifier } from "@/utils/id"

export type ForkActivation = {
  /** Receives the provisional chat immediately, before the machine confirms it. */
  open: (sessionID: string) => void
  /** Runs once the machine owns the chat. */
  created?: (session: Session) => void
  /**
   * Runs once the chat fails to start; the caller reports the error. `kept`
   * means the machine may already hold the chat, so it stays as an ordinary
   * chat whose next sync loads it by ID or finds it missing; otherwise the
   * provisional chat was discarded.
   */
  failed: (sessionID: string, error: unknown, kept: boolean) => void
}

export type StarterActivation = ForkActivation & { starter: StarterMessage }

export type MessageForkActivation = ForkActivation & {
  workspaceID: string
  sessionID: string
  messageID: string
  /** Empty keeps the fork on the automatic title lifecycle. */
  title: string
}

type SeedOptions = { isNew?: boolean; provisional?: boolean; messagesPending?: boolean }

type TranscriptItems = Array<{ info: Message; parts: Part[] }>

type TranscriptSnapshot = {
  items: TranscriptItems
  complete: boolean
  cursor?: string
}

const bySourceID = (a: Part, b: Part) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/**
 * How many messages ending at a starter are kept ready to copy. The fork shows
 * them at once; the machine's answer then fills in older history.
 */
const STARTER_PAGE_SIZE = 50
/** A page keeps its newest messages that fit; the starter message itself always stays. */
const STARTER_PAGE_BYTES = 256 * 1024
/** Pages kept in memory past this total are not kept; those starters wait for the machine. */
const STARTER_PAGES_TOTAL_BYTES = 32 * 1024 * 1024
/** Pages loading from the machine at once, newest starters first. */
const STARTER_PAGE_LOADS = 3
const STARTER_PAGE_RETRY_MAX_MS = 60_000

type StarterPageEntry = {
  sessionID: string
  messageID: string
  items?: TranscriptItems
  bytes: number
  loading?: AbortController
  retry?: ReturnType<typeof setTimeout>
  failures: number
  /** The machine refused the page, or it did not fit; the starter waits for the machine. */
  settled?: boolean
}

/**
 * A plain copy of the page's newest messages within the byte budget. Plain
 * values can be stored, unlike the reactive store's proxies.
 */
function boundedPage(items: readonly { info: Message; parts: readonly Part[] }[]) {
  const kept: TranscriptItems = []
  let bytes = 0
  for (const item of items.slice(-STARTER_PAGE_SIZE).reverse()) {
    const json = JSON.stringify(item)
    if (kept.length && bytes + json.length > STARTER_PAGE_BYTES) break
    kept.push(JSON.parse(json) as TranscriptItems[number])
    bytes += json.length
  }
  return { items: kept.reverse(), bytes }
}

/** A refusal that retrying will not change. */
const permanentFailure = (error: unknown) =>
  error instanceof HttpError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429

type SourceTranscript = {
  messages: readonly Message[] | undefined
  parts: (messageID: string) => readonly Part[] | undefined
}

/**
 * The copy the machine makes of a chat's messages through `through` into fork
 * `forkID`, built from the loaded transcript under the IDs the machine derives
 * for it, so the fork shows at once and the machine's answer replaces it in
 * place. Undefined when the chat is not loaded through that message.
 *
 * Two fields differ until that answer: a turn's file-change summary, which the
 * fork does not have yet, and part content URLs, which keep naming the source
 * because the fork's own URLs do not resolve before the machine makes it.
 */
export function forkTranscript(input: SourceTranscript & {
  forkID: string
  through: string
}): TranscriptItems | undefined {
  const boundary = input.messages?.find((message) => message.id === input.through)
  if (!input.messages || !boundary) return undefined
  const copy = (id: string) => Identifier.derived(input.forkID, id)
  return input.messages
    .filter((message) => cmpMessage(message, boundary) <= 0)
    .map((message) => {
      const messageID = copy(message.id)
      const info: TurnDiffMessage = { ...message, id: messageID, sessionID: input.forkID }
      delete info.turnDiff
      if (info.role === "assistant" && info.parentID) info.parentID = copy(info.parentID)
      // The machine copies a message's parts in source-ID order.
      const parts = [...(input.parts(message.id) ?? [])].filter((part) => part.id).sort(bySourceID)
      return {
        info,
        parts: parts.map((part) => ({ ...part, id: copy(part.id), sessionID: input.forkID, messageID })),
      }
    })
}

/** Mirrors the session the machine creates so the composer starts with the model the fork will use. */
function provisionalFork(input: {
  id: string
  workspaceID: string
  title: string
  source: Session | undefined
  directory: string | undefined
  model: Pick<Session, "providerID" | "modelID" | "variant">
}): Session {
  const now = Date.now()
  return {
    id: input.id,
    slug: input.id,
    projectID: input.source?.projectID ?? "default",
    workspaceID: input.workspaceID,
    directory: input.source?.directory ?? input.directory ?? "",
    title: input.title,
    version: input.source?.version ?? "2",
    agent: input.source?.agent || "build",
    // The machine keeps a TUI chat's surface and makes every other fork a chat.
    surface: input.source?.surface === "tui" ? "tui" : "chat",
    providerID: input.model.providerID,
    modelID: input.model.modelID,
    variant: input.model.variant || undefined,
    time: { created: now, updated: now, activity: now },
  }
}

/**
 * Owns chat forks for one machine. A fork opens optimistically and outlives
 * the menu, dialog, or surface that started it; only the machine scope cancels
 * it, so a fork the machine creates is never silently dropped.
 */
export function createForkActivationController(input: {
  clientFor: (workspaceID: string) => MuxClient
  getSession: (sessionID: string) => Session | undefined
  workspaceDirectory: (workspaceID: string) => string | undefined
  seed: (workspaceID: string, session: Session, options: SeedOptions) => void
  /** The loaded transcript a fork copies; undefined when the chat is not loaded. */
  messages: (sessionID: string) => readonly Message[] | undefined
  parts: (messageID: string) => readonly Part[] | undefined
  /** Shows a fork's copy before the machine confirms it. */
  installProvisional: (workspaceID: string, sessionID: string, items: TranscriptItems) => void
  install: (workspaceID: string, session: Session, snapshot: TranscriptSnapshot) => void
  discard: (workspaceID: string, sessionID: string) => void
  /** This device's cache of starter pages, for the machine `pageScope` names. */
  pages: StarterPageRepository
  pageScope: string
}) {
  const lifetime = new AbortController()
  /** The page each of the machine's starters forks with, by `starterKey` of its transcript. */
  const starterPages = new Map<string, StarterPageEntry>()
  const starterKey = (sessionID: string, messageID: string) => `${sessionID}\u0000${messageID}`
  /** What a starter's fork copies: the chat it was saved in, through its message. */
  const starterCopy = (starter: StarterMessage) => ({ sessionID: starter.sessionID, messageID: starter.messageID })
  /** The machine's starters, newest first; undefined until they are known. */
  let starters: readonly StarterMessage[] | undefined
  let hydrated = false
  let loads = 0
  let keptBytes = 0

  const dropPage = (key: string, entry: StarterPageEntry) => {
    entry.loading?.abort()
    clearTimeout(entry.retry)
    keptBytes -= entry.bytes
    starterPages.delete(key)
  }

  const keepPage = (entry: StarterPageEntry, items: readonly { info: Message; parts: readonly Part[] }[]) => {
    const page = boundedPage(items)
    if (!page.items.some((item) => item.info.id === entry.messageID)) return false
    if (keptBytes + page.bytes > STARTER_PAGES_TOTAL_BYTES) {
      entry.settled = true
      return true
    }
    entry.items = page.items
    entry.bytes = page.bytes
    keptBytes += page.bytes
    void input.pages.write(input.pageScope, { sessionID: entry.sessionID, messageID: entry.messageID, items: page.items })
    return true
  }

  const loadPage = (key: string, entry: StarterPageEntry, workspaceID: string) => {
    const request = new AbortController()
    entry.loading = request
    loads += 1
    loadTranscriptThrough(input.clientFor(workspaceID), {
      sessionID: entry.sessionID,
      messageID: entry.messageID,
      limit: STARTER_PAGE_SIZE,
      signal: request.signal,
    }).then(
      (items) => {
        if (starterPages.get(key) !== entry) return
        // A page without its starter cannot be copied; the machine decides at fork time.
        if (!keepPage(entry, items)) entry.settled = true
      },
      (error: unknown) => {
        if (request.signal.aborted || starterPages.get(key) !== entry) return
        if (permanentFailure(error)) {
          entry.settled = true
          return
        }
        entry.failures += 1
        entry.retry = setTimeout(() => {
          entry.retry = undefined
          pumpPages()
        }, Math.min(STARTER_PAGE_RETRY_MAX_MS, 1_000 * 2 ** (entry.failures - 1)))
      },
    ).finally(() => {
      loads -= 1
      if (entry.loading === request) entry.loading = undefined
      pumpPages()
    })
  }

  /** Readies every starter's page from the machine, for when its chat is not loaded here. */
  const pumpPages = () => {
    if (lifetime.signal.aborted || !hydrated || !starters) return
    for (const starter of starters) {
      const copy = starterCopy(starter)
      const key = starterKey(copy.sessionID, copy.messageID)
      const entry = starterPages.get(key)
      if (!entry || entry.items || entry.settled || entry.loading || entry.retry) continue
      if (loads >= STARTER_PAGE_LOADS) return
      loadPage(key, entry, starter.workspaceID)
    }
  }

  const reconcilePages = () => {
    if (lifetime.signal.aborted || !hydrated || !starters) return
    const wanted = new Map(starters.map((starter) => {
      const copy = starterCopy(starter)
      return [starterKey(copy.sessionID, copy.messageID), copy] as const
    }))
    for (const [key, entry] of starterPages) {
      if (wanted.has(key)) continue
      dropPage(key, entry)
      void input.pages.remove(input.pageScope, entry.sessionID, entry.messageID)
    }
    for (const [key, copy] of wanted) {
      if (!starterPages.has(key)) starterPages.set(key, { ...copy, bytes: 0, failures: 0 })
    }
    pumpPages()
  }

  void input.pages.list(input.pageScope).then((stored) => {
    if (lifetime.signal.aborted) return
    for (const page of stored) {
      const key = starterKey(page.sessionID, page.messageID)
      if (starterPages.has(key)) continue
      const bytes = JSON.stringify(page.items).length
      starterPages.set(key, { sessionID: page.sessionID, messageID: page.messageID, items: page.items, bytes, failures: 0 })
      keptBytes += bytes
    }
  }).finally(() => {
    hydrated = true
    reconcilePages()
  })

  /** The chat's loaded transcript when it reaches the message, else the starter's page. */
  const sourceTranscript = (sessionID: string, messageID: string): SourceTranscript => {
    const loaded = input.messages(sessionID)
    if (loaded?.some((message) => message.id === messageID)) return { messages: loaded, parts: input.parts }
    const items = starterPages.get(starterKey(sessionID, messageID))?.items
    if (!items) return { messages: undefined, parts: () => undefined }
    const parts = new Map(items.map((item) => [item.info.id, item.parts]))
    return { messages: items.map((item) => item.info), parts: (id) => parts.get(id) }
  }

  const launch = (
    workspaceID: string,
    provisional: Session,
    /** What the fork copies: a chat through one of its messages. */
    source: { sessionID: string; messageID: string },
    request: ForkActivation,
    start: (signal: AbortSignal) => Promise<SessionSnapshot>,
    rejected?: (error: unknown) => void,
  ) => {
    const copy = forkTranscript({
      forkID: provisional.id,
      ...sourceTranscript(source.sessionID, source.messageID),
      through: source.messageID,
    })
    input.seed(workspaceID, provisional, { isNew: true, provisional: true, messagesPending: true })
    if (copy) input.installProvisional(workspaceID, provisional.id, copy)
    request.open(provisional.id)
    void start(lifetime.signal).then(
      (result) => {
        if (lifetime.signal.aborted) return
        // Install unconditionally: `created` is optional and must not gate it.
        input.install(workspaceID, result.session, {
          items: result.messages,
          complete: !result.hasMore,
          cursor: result.nextCursor,
        })
        request.created?.(result.session)
      },
      (error: unknown) => {
        if (lifetime.signal.aborted) return
        // An earlier attempt may have created the chat, so it is kept: seeding
        // without `provisional` settles it as an ordinary chat.
        const kept = error instanceof CreateRequestError && error.outcomeUnknown
        if (kept) input.seed(workspaceID, input.getSession(provisional.id) ?? provisional, {})
        else input.discard(workspaceID, provisional.id)
        rejected?.(error)
        request.failed(provisional.id, error, kept)
      },
    )
    return provisional.id
  }

  return {
    /**
     * Sets the machine's starters, newest first. Every starter's page is kept
     * ready on this device, so starting one shows its history at once even
     * when its chat is not loaded; pages of removed starters are dropped.
     */
    setStarters(next: readonly StarterMessage[]) {
      if (lifetime.signal.aborted) return
      starters = next
      reconcilePages()
    },

    startFromStarter(request: StarterActivation) {
      if (lifetime.signal.aborted) return
      const { starter } = request
      const workspaceID = starter.workspaceID
      const source = input.getSession(starter.sessionID)
      // The machine applies the starter's saved model, else the source chat's.
      const model = starter.providerID && starter.modelID ? starter : source ?? {}
      const provisional = provisionalFork({
        id: Identifier.ascending("session"),
        workspaceID,
        title: "New Chat",
        source,
        directory: input.workspaceDirectory(workspaceID),
        model,
      })
      return launch(
        workspaceID,
        provisional,
        starterCopy(starter),
        request,
        (signal) => startSessionFromStarter(input.clientFor(workspaceID), {
          sourceSessionID: starter.sessionID,
          messageID: starter.messageID,
          sessionID: provisional.id,
          signal,
        }),
        (error) => {
          if (error instanceof CreateRequestError && error.status === 404) {
            removeStarterMessage(starter.sessionID, starter.messageID)
          }
        },
      )
    },

    forkAtMessage(request: MessageForkActivation) {
      if (lifetime.signal.aborted) return
      const { workspaceID } = request
      const source = input.getSession(request.sessionID)
      const provisional = provisionalFork({
        id: Identifier.ascending("session"),
        workspaceID,
        title: request.title || "New Chat",
        source,
        directory: input.workspaceDirectory(workspaceID),
        model: source ?? {},
      })
      return launch(
        workspaceID,
        provisional,
        { sessionID: request.sessionID, messageID: request.messageID },
        request,
        (signal) => forkSessionAtMessage(input.clientFor(workspaceID), {
          sessionID: request.sessionID,
          messageID: request.messageID,
          title: request.title,
          forkID: provisional.id,
          signal,
        }),
      )
    },

    dispose() {
      lifetime.abort()
      for (const [key, entry] of starterPages) dropPage(key, entry)
    },
  }
}
